import React, { useEffect } from "react";
import { GlobalStyles } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";

import galleryTheme, { tokens } from "./galleryTheme";



const FONT_LINK_ID = "lg-gallery-fonts";
const FONT_HREF =
  // Inter (body/UI) + Baskervville (logo-style headings; regular and italic).
  "https://fonts.googleapis.com/css2?family=Baskervville:ital@0;1&family=Inter:wght@400;500;600&display=swap";

/** Loads the two web fonts once. Nothing else on the page is changed. */
function useGalleryFonts() {
  useEffect(() => {
    if (typeof document === "undefined" || document.getElementById(FONT_LINK_ID)) return;

    const preconnect = document.createElement("link");
    preconnect.rel = "preconnect";
    preconnect.href = "https://fonts.gstatic.com";
    preconnect.crossOrigin = "anonymous";

    const stylesheet = document.createElement("link");
    stylesheet.id = FONT_LINK_ID;
    stylesheet.rel = "stylesheet";
    stylesheet.href = FONT_HREF;

    document.head.appendChild(preconnect);
    document.head.appendChild(stylesheet);
  }, []);
}

const scopedStyles = {
  ".lg-gallery": {
    fontFamily: tokens.font.body,
    color: tokens.color.text,
    WebkitFontSmoothing: "antialiased",
    MozOsxFontSmoothing: "grayscale",
  },
  // A stylesheet elsewhere in the app sets `p { margin: 5px 0 !important }` on EVERY page.
  // Inside the redesigned blocks we control spacing ourselves (with `gap`), so neutralise it here.
  ".lg-gallery p": { marginTop: "0 !important", marginBottom: "0 !important" },
  ".lg-gallery h1, .lg-gallery h2, .lg-gallery h3, .lg-gallery h4": { margin: 0 },
  ".lg-gallery img": { maxWidth: "100%" },
  // Visible keyboard focus on every interactive element (mouse clicks are unaffected).
  ".lg-gallery :focus-visible": {
    outline: `2px solid ${tokens.color.ink}`,
    outlineOffset: "2px",
  },
  ".lg-gallery.lg-gallery--dark :focus-visible, .lg-gallery .lg-gallery--dark :focus-visible": {
    outlineColor: "#FFFFFF",
  },
  // Screen-reader-only text (labels that should not be visible).
  ".lg-sr-only": {
    position: "absolute !important",
    width: "1px",
    height: "1px",
    padding: 0,
    margin: "-1px",
    overflow: "hidden",
    clip: "rect(0, 0, 0, 0)",
    whiteSpace: "nowrap",
    border: 0,
  },
  "@media (prefers-reduced-motion: reduce)": {
    ".lg-gallery *, .lg-gallery *::before, .lg-gallery *::after": {
      transitionDuration: "0.01ms !important",
      animationDuration: "0.01ms !important",
      scrollBehavior: "auto !important",
    },
  },
};

export default function GalleryScope({ children }) {
  useGalleryFonts();

  return (
    <ThemeProvider theme={galleryTheme}>
      <GlobalStyles styles={scopedStyles} />
      {children}
    </ThemeProvider>
  );
}
